import { makeAutoObservable, runInAction } from 'mobx'
import axios, { CanceledError } from 'axios'
import { totalsToPercents } from 'shared/lib/totalsToPercents'
import { logger } from 'shared/lib'
import { errorHandler } from 'shared/api'
import { RequestCancelAxios } from 'shared/api/lib'
import {
  type ChatbotAnalyticListItem,
  ChatbotApi,
  ChatbotInstructionType,
  ChatbotStatus,
  type IChatbotFullData,
} from 'entities/Chatbot'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { fetchSendFromInfo, type IParamsSendFrom, type ISendFromInfo } from 'entities/SendFrom'
import {
  unifiedAgentActivatedLogEvent,
  unifiedAgentDeletedLogEvent,
  unifiedAgentDisabledLogEvent,
} from 'entities/AIAgent/actions/unifiedAgentEvents'
import { chatbotOperation } from 'pages/chatbot/store/chatbotOperationStore'
import { AgentKnowledgeBaseStore } from 'pages/chatbot/ui/AgentKnowledgeBase/store/AgentKnowledgeBaseStore.ts'

export class ChatbotAnalyticDetailStore {
  updating = false
  initialLoading = true
  isAccessDenied = false

  name = ''
  active = false
  status = ChatbotStatus.Disabled

  completedCount = 0
  terminatedCount = 0
  fallbackCount = 0
  totalCount = 0
  inProgressCount = 0
  qualifiedCount = 0
  disqualifiedCount = 0
  notQualifiedCount = 0
  canceledCount = 0
  rescheduledCount = 0
  quietHoursMessagesCount = 0
  skippedCount = 0
  unifiedBookingCount = 0
  handoffContactsCount = 0

  completedPercent = 0
  terminatedPercent = 0
  fallbackPercent = 0
  inProgressPercent = 0
  qualifiedPercent = 0
  disqualifiedPercent = 0
  notQualifiedPercent = 0
  canceledPercent = 0
  rescheduledPercent = 0
  quietHoursMessagesPercent = 0
  skippedPercent = 0
  unifiedBookingPercent = 0
  handoffContactsPercent = 0

  private _data: IChatbotFullData | null = null
  private _request: Promise<void> | null = null
  private _type: ChatbotInstructionType | null = null
  private _sendFromInfoLoading = new Set<string>()
  private _sendFromInfoMap: Map<string, ISendFromInfo | null> = new Map()
  private _cancelRequest = new RequestCancelAxios()
  readonly agentKnowledgeBaseStore = new AgentKnowledgeBaseStore()

  constructor(public id: number, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get data() {
    return this._data
  }

  get type() {
    return this._type
  }

  get featureFlags() {
    return this._entities.featureFlagsStore
  }

  get isLiveDocumentIssue() {
    if (this._loadingInitAgentKnowledgeBase) {
      return false
    }
    return this._data?.is_live_document_issue ?? false
  }

  get qualifyEnabled() {
    return this._data?.settings?.qualify_enabled?.value ?? false
  }
  get smartBookingEnabled() {
    return this._data?.settings?.smart_booking_enabled?.value ?? false
  }

  get isBookingEnabled() {
    return this._data?.settings?.book_appointment_enabled?.value ?? false
  }
  get isHandoffEnabled() {
    return this._data?.settings?.handoff_enabled?.value ?? false
  }

  init = () => {
    if (this._request) return this._request

    this._request = this._loadData()

    return this._request
  }

  updateStatus = async (isActive: boolean) => {
    this.updating = true

    const isUnified = this._type === ChatbotInstructionType.Unified
    const isUnifiedDisabling = !isActive && isUnified
    const isUnifiedActivating = isActive && isUnified
    const wasDraft = this.status === ChatbotStatus.Draft

    try {
      const { data } = (await chatbotOperation.updateStatus(this.id, isActive)) ?? {}

      runInAction(() => {
        if (!data) return

        this._data = data
        this.active = isActive
        this.status = data.status
      })

      if (data && isUnifiedDisabling) {
        unifiedAgentDisabledLogEvent(this._entities, {
          agent_id: this.id,
        })
      }

      if (data && isUnifiedActivating) {
        unifiedAgentActivatedLogEvent(this._entities, {
          agent_id: this.id,
          activated_from_draft: wasDraft,
        })
      }
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this.updating = false
      })
    }
  }

  refresh = () => {
    this._request = this._loadData()
  }

  delete = () =>
    chatbotOperation.delete(this.id).then((deleted) => {
      if (deleted && this._type === ChatbotInstructionType.Unified) {
        unifiedAgentDeletedLogEvent(this._entities, {
          agent_id: this.id,
          was_active: this.active,
        })
      }

      return deleted
    })

  dispose = () => {
    this._cancelRequest.reset()
  }

  private _loadingInitAgentKnowledgeBase = false
  private _loadData = async () => {
    this.initialLoading = true
    try {
      const { data } = await ChatbotApi.getChatbot(this.id)

      if (data.settings?.use_knowledge_base?.value && data.knowledge_base_documents) {
        runInAction(() => {
          this._loadingInitAgentKnowledgeBase = true
        })
        this.agentKnowledgeBaseStore
          .initKnowledgeBase()
          .then(() => {
            if (data.knowledge_base_documents) {
              return this.agentKnowledgeBaseStore.agentDocumentsStore.init(
                data.knowledge_base_documents
              )
            }
          })
          .finally(() => {
            runInAction(() => {
              this._loadingInitAgentKnowledgeBase = false
            })
          })
      }

      runInAction(() => {
        this._data = data
        this._setState(data)
        this._setTotals(data)
        this.initialLoading = false
      })
    } catch (error) {
      errorHandler<void>(error as Error).then(({ type, error }) => {
        if (type === 'axios-error') {
          if (error.status === 403) {
            runInAction(() => {
              this.isAccessDenied = true
            })
          }
        }
      })

      runInAction(() => {
        this.initialLoading = error instanceof CanceledError
      })

      logger.error(error)
    }
  }

  private _setState = (data: IChatbotFullData) => {
    this.active = data.is_active
    this.name = data.name
    this.status = data.status
    this._type = data.type
  }

  private _setTotals = (data: IChatbotFullData) => {
    this.completedCount = data.completed_contacts_count
    this.terminatedCount = data.terminated_contacts_count
    this.fallbackCount = data.fallback_contacts_count
    this.inProgressCount = data.in_progress_contacts_count
    this.qualifiedCount = data.qualified_contacts_count
    this.disqualifiedCount = data.disqualified_contacts_count
    this.notQualifiedCount = data.not_qualified_contacts_count
    this.canceledCount = data.cancelled_appointments_count
    this.rescheduledCount = data.rescheduled_appointments_count
    this.totalCount = data.total_contacts_count
    this.quietHoursMessagesCount = data.quiet_hours_messages_count ?? 0
    this.skippedCount = data.skipped_contacts_count
    this.unifiedBookingCount = data.booked_appointments_count
    this.handoffContactsCount = data.handoff_contacts_count

    if (this.type === ChatbotInstructionType.Unified) {
      const [
        completedPercent,
        terminatedPercent,
        fallbackPercent,
        inProgressPercent,
        skippedPercent,
      ] = totalsToPercents(
        [
          this.completedCount,
          this.terminatedCount,
          this.fallbackCount,
          this.inProgressCount,
          this.skippedCount,
        ],
        this.totalCount
      )
      const [canceledPercent, rescheduledPercent, unifiedBookingPercent, handoffContactsPercent] =
        totalsToPercents(
          [
            this.canceledCount,
            this.rescheduledCount,
            this.unifiedBookingCount,
            this.handoffContactsCount,
          ],
          this.totalCount
        )

      this.completedPercent = completedPercent
      this.terminatedPercent = terminatedPercent
      this.fallbackPercent = fallbackPercent
      this.inProgressPercent = inProgressPercent
      this.canceledPercent = canceledPercent
      this.rescheduledPercent = rescheduledPercent
      this.skippedPercent = skippedPercent
      this.unifiedBookingPercent = unifiedBookingPercent
      this.handoffContactsPercent = handoffContactsPercent
    } else {
      const [
        completedPercent,
        terminatedPercent,
        fallbackPercent,
        inProgressPercent,
        canceledPercent,
        rescheduledPercent,
        skippedPercent,
        unifiedBookingPercent,
      ] = totalsToPercents(
        [
          this.completedCount,
          this.terminatedCount,
          this.fallbackCount,
          this.inProgressCount,
          this.canceledCount,
          this.rescheduledCount,
          this.skippedCount,
          this.unifiedBookingCount,
        ],
        this.totalCount
      )

      this.completedPercent = completedPercent
      this.terminatedPercent = terminatedPercent
      this.fallbackPercent = fallbackPercent
      this.inProgressPercent = inProgressPercent
      this.canceledPercent = canceledPercent
      this.rescheduledPercent = rescheduledPercent
      this.skippedPercent = skippedPercent
      this.unifiedBookingPercent = unifiedBookingPercent
    }

    if (this.featureFlags.qualify) {
      const [qualifiedPercent, disqualifiedPercent, notQualifiedPercent] = totalsToPercents(
        [this.qualifiedCount, this.disqualifiedCount, this.notQualifiedCount],
        this.totalCount
      )
      this.qualifiedPercent = qualifiedPercent
      this.disqualifiedPercent = disqualifiedPercent
      this.notQualifiedPercent = notQualifiedPercent
    }

    if (this.quietHoursMessagesCount && this.totalCount) {
      this.quietHoursMessagesPercent = Math.round(
        (this.quietHoursMessagesCount / this.totalCount) * 100
      )
    }
  }

  get isSendFromDataLoading() {
    return this._sendFromInfoLoading.size > 0
  }

  loadAllSendFromInfo = async (rows: ChatbotAnalyticListItem[]) => {
    this._cancelRequest.init()

    const sendFromPromises = rows
      .flatMap((row) => {
        if (!row.sendFrom) return []

        const sendFromId = this.getSendFromCacheKey(row.sendFrom)
        return sendFromId ? [{ row, sendFromId }] : []
      })
      .map(async ({ row, sendFromId }) => {
        if (this._sendFromInfoMap.has(sendFromId) || this._sendFromInfoLoading.has(sendFromId))
          return

        this._sendFromInfoLoading.add(sendFromId)

        try {
          const info = await fetchSendFromInfo(
            row.sendFrom || null,
            this._entities,
            this._cancelRequest.token
          )

          runInAction(() => {
            this._sendFromInfoMap.set(sendFromId, info)
            this._sendFromInfoLoading.delete(sendFromId)
          })
        } catch (error) {
          runInAction(() => {
            this._sendFromInfoLoading.delete(sendFromId)
          })

          if (!axios.isCancel(error)) logger.error(error)
        }
      })

    await Promise.all(sendFromPromises)
  }

  getSendFromInfoSync = (sendFrom: IParamsSendFrom) => {
    const sendFromId = this.getSendFromCacheKey(sendFrom)

    if (!sendFromId) return null

    return this._sendFromInfoMap.get(sendFromId) || null
  }

  getSendFromCacheKey = (sendFrom: IParamsSendFrom | null): string | null => {
    if (!sendFrom) return null

    switch (sendFrom.type) {
      case 'from_smart_inbox':
        const teamId = sendFrom.options.team_id
        const smartOption = sendFrom.options.smart_option

        if (!teamId || !smartOption) return null

        return `smart_${teamId}_${smartOption}`

      case 'from_number':
        if (sendFrom.options.number_id) {
          return `number_${sendFrom.options.number_id}`
        }
        if (sendFrom.options.team_id) {
          return `team_${sendFrom.options.team_id}`
        }

        return null

      case 'from_integration':
        const ownerProperty = sendFrom.options.owner_property
        if (!ownerProperty) return null

        return `integration_${ownerProperty}`

      case 'from_contact_owner':
        if (sendFrom.options.team_id) {
          return `contact_owner_${sendFrom.options.team_id}`
        }
        return 'contact_owner'

      default:
        return null
    }
  }
}
